export interface DayQuery {
  readonly year: number | undefined;
  readonly month: number;
  readonly day: number;
}

export interface SearchQuery {
  readonly days: readonly DayQuery[];
  readonly words: readonly string[];
}

type Parts = readonly (string | undefined)[];

interface DatePattern {
  readonly regex: RegExp;
  readonly read: (parts: Parts, today: number) => DayQuery | undefined;
}

const DAY_MS = 86_400_000;
const CENTURY = 2000;
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;
const MONTH_NAMES = 'january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept|sep|oct|nov|dec';
const ORDINAL = '(?:st|nd|rd|th)?';

function dayQuery(day: string | undefined, month: number | undefined, year: string | undefined): DayQuery | undefined {
  const dayNumber = Number(day);
  if (month === undefined || month < 1 || month > 12 || !(dayNumber >= 1 && dayNumber <= 31)) return undefined;
  const yearNumber = year === undefined ? undefined : Number(year);
  return { year: yearNumber !== undefined && yearNumber < 100 ? CENTURY + yearNumber : yearNumber, month, day: dayNumber };
}

const monthOf = (name: string | undefined): number | undefined => {
  const index = MONTHS.findIndex((month) => month === name?.slice(0, 3));
  return index < 0 ? undefined : index + 1;
};

const dayOf = (wallMs: number): DayQuery => {
  const date = new Date(wallMs);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
};

const PATTERNS: readonly DatePattern[] = [
  { regex: /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g, read: ([year, month, day]) => dayQuery(day, Number(month), year) },
  { regex: /\b(\d{1,2})\.(\d{1,2})\.(\d{4}|\d{2})?(?!\d)/g, read: ([day, month, year]) => dayQuery(day, Number(month), year) },
  { regex: new RegExp(`\\b(\\d{1,2})${ORDINAL}\\.?\\s*(${MONTH_NAMES})\\b\\.?(?:\\s+(\\d{4})\\b)?`, 'g'), read: ([day, month, year]) => dayQuery(day, monthOf(month), year) },
  { regex: new RegExp(`\\b(${MONTH_NAMES})\\.?\\s+(\\d{1,2})${ORDINAL}\\b(?:,?\\s+(\\d{4})\\b)?`, 'g'), read: ([month, day, year]) => dayQuery(day, monthOf(month), year) },
  { regex: /\b(today|yesterday)\b/g, read: ([word], today) => dayOf(word === 'today' ? today : today - DAY_MS) },
];

export function parseSearch(query: string, today: number): SearchQuery {
  const days: DayQuery[] = [];
  const rest = PATTERNS.reduce(
    (text, { regex, read }) =>
      text.replace(regex, (match: string, ...groups: unknown[]) => {
        const found = read(groups.slice(0, 3).map((group) => (typeof group === 'string' ? group : undefined)), today);
        if (found) days.push(found);
        return found ? ' ' : match;
      }),
    query.toLowerCase(),
  );
  return { days, words: rest.split(/\s+/).filter(Boolean) };
}

export function onDay(query: DayQuery, wallMs: number): boolean {
  const { year, month, day } = dayOf(wallMs);
  return day === query.day && month === query.month && (query.year === undefined || year === query.year);
}
